import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import type { Dayjs } from "dayjs";
import range from "lodash/range";
import { getCalculationExchangeRate } from "../../calculations/calculationUtils";
import type { Currency } from "../../data/BaseEntity";
import type { Price } from "../../misc/price";
import { useProductsTotalPrice } from "../../sharedComponents/products/price/useProductsTotalPrice";

const DAYS_RANGE = { from: -4, to: 5 };
const days = range(DAYS_RANGE.from, DAYS_RANGE.to);

function arePricesEmpty(prices: Record<number, Price>): boolean {
  return Object.entries(prices)
    .filter(([key]) => Number(key) !== 0)
    .every(([, price]) => !price.isPresent());
}

export function DynamicPricing(props: {
  selectedProducts: { id: string; date: string; options: { id: string; quantity: number }[] }[];
  availableProducts: { id: string; currency: Currency }[];
  currency: Currency | undefined;
  margin: number;
  numberOfPeople: number;
  numberOfPaidPeople: number;
  startDate: Dayjs | null;
  onClick(date: Dayjs): void;
  isEditMode: boolean;
}) {
  const {
    selectedProducts,
    availableProducts,
    currency,
    margin,
    numberOfPeople,
    numberOfPaidPeople,
    startDate,
    isEditMode,
  } = props;

  const productsWithCurrency = selectedProducts.map((p) => ({
    ...p,
    currency: availableProducts.find((pr) => pr.id === p.id)?.currency,
  }));

  const exchangeRate = currency
    ? getCalculationExchangeRate(
        selectedProducts,
        currency,
        productsWithCurrency.filter((p): p is typeof p & { currency: Currency } => p.currency !== undefined),
      )
    : undefined;

  const { prices } = useProductsTotalPrice(
    currency ? productsWithCurrency : [],
    currency ?? "PLN",
    margin,
    exchangeRate,
    DAYS_RANGE,
    numberOfPeople,
  );

  const isEmpty = arePricesEmpty(prices);
  if (!currency || selectedProducts.length === 0 || !startDate || isEmpty) return null;

  const basePrice = prices[0];

  return (
    <Box sx={{ mb: 4, mt: 3 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          mb: 2,
        }}
      >
        Ceny wycieczki w alternatywnych terminach
      </Typography>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          overflowX: "auto",
          pt: 1,
          pb: 1,
          px: 0.5,
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.2)", borderRadius: 3 },
        }}
      >
        {days.map((d) => {
          const price = prices[d];
          const isSelected = d === 0;
          const date = startDate.add(d, "days");

          const dayLabel = date.format("dddd DD.MM");

          const diff =
            price?.isPresent() &&
            basePrice?.isPresent() &&
            typeof price.value === "number" &&
            typeof basePrice.value === "number"
              ? price.value - basePrice.value
              : null;
          const diffPercent =
            diff !== null && typeof basePrice.value === "number" && basePrice.value > 0
              ? Math.round((diff / basePrice.value) * 100)
              : null;

          return (
            <Box key={d} sx={{ minWidth: { xs: 120, md: 150 }, flex: 1 }}>
              <Paper
                elevation={isSelected ? 4 : 0}
                onClick={isSelected || !isEditMode ? undefined : () => props.onClick(date)}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  bgcolor: isSelected ? "primary.main" : "background.paper",
                  color: isSelected ? "white" : "text.primary",
                  border: isSelected ? "2px solid" : "1px solid",
                  borderColor: isSelected ? "primary.main" : "rgba(0,0,0,0.1)",
                  borderRadius: 2,
                  cursor: isEditMode && !isSelected ? "pointer" : undefined,
                  transition: "all 0.2s ease",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  height: "100%",
                  "&:hover": isEditMode
                    ? {
                        bgcolor: isSelected ? "primary.dark" : "rgba(13, 110, 253, 0.04)",
                        borderColor: isSelected ? "primary.dark" : "primary.main",
                        transform: "translateY(-2px)",
                      }
                    : undefined,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: isSelected ? "bold" : 500,
                    opacity: isSelected ? 1 : 0.7,
                    mb: 1,
                    display: "block",
                  }}
                >
                  {dayLabel}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    mb: 0,
                    fontSize: { xs: "1.05rem", md: "1.25rem" },
                  }}
                >
                  {price?.isPresent() ? price.toDisplayValue(currency) : "-"}
                </Typography>
                <Typography
                  variant="caption"
                  color={isSelected ? "rgba(255,255,255,0.8)" : "text.secondary"}
                  sx={{ mb: 1.5, display: "block" }}
                >
                  {price?.isPresent() && numberOfPaidPeople > 0
                    ? `${price.divideBy(numberOfPaidPeople).round().toDisplayValue(currency)}/os.`
                    : "-"}
                </Typography>
                {isSelected ? (
                  <Chip
                    label="Wybrany"
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      fontWeight: "bold",
                      height: 24,
                      fontSize: "0.75rem",
                      px: 0.5,
                    }}
                  />
                ) : (
                  <Chip
                    label={diffPercent !== null ? (diff! > 0 ? `+${diffPercent}%` : `${diffPercent}%`) : "-"}
                    size="small"
                    sx={{
                      bgcolor: diffPercent !== null ? (diff! > 0 ? "error.main" : "success.main") : "rgba(0,0,0,0.05)",
                      color: diffPercent !== null ? "white" : "text.disabled",
                      fontWeight: "bold",
                      height: 24,
                      fontSize: "0.75rem",
                      px: 0.5,
                    }}
                  />
                )}
              </Paper>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
